<template>
  <div class="tw-w-full" ref="root">
    <label
      v-if="textLabel"
      class="tw-block tw-mb-1 tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-slate-500"
    >
      {{ textLabel }}
    </label>

    <div class="tw-relative tw-w-full">
      <button
        ref="buttonEl"
        type="button"
        class="tw-w-full tw-flex tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-px-3 tw-py-2 tw-text-left tw-shadow-sm tw-shadow-slate-900/5 tw-transition"
        :class="[
          disabled
            ? 'tw-bg-slate-50 tw-border-slate-200 tw-text-slate-400 tw-cursor-not-allowed'
            : 'tw-bg-white/70 tw-border-slate-200/70 tw-text-slate-900 hover:tw-bg-white',
          isOpen && !disabled ? 'tw-ring-4 tw-ring-indigo-500/15 tw-border-indigo-500/55' : '',
        ]"
        :disabled="disabled"
        @click="toggle"
      >
        <span class="tw-flex-1 tw-min-w-0 tw-truncate" :class="selectedLabel ? 'tw-text-slate-900' : 'tw-text-slate-500'">
          {{ selectedLabel || placeholder || 'Select…' }}
        </span>

        <span class="tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0">
          <font-awesome-icon
            icon="chevron-down"
            class="tw-h-4 tw-w-4 tw-text-slate-500 tw-transition-transform"
            :class="isOpen ? 'tw-rotate-180' : ''"
          />
        </span>
      </button>

      <Teleport to="body">
        <div
          v-if="isOpen && !disabled"
          ref="menuEl"
          class="tw-fixed tw-z-[3000] tw-overflow-hidden tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white tw-shadow-xl tw-shadow-slate-900/10"
          :style="menuStyle"
        >
          <div class="tw-max-h-64 tw-overflow-auto tw-p-1">
            <button
              v-for="(opt, idx) in safeOptions"
              :key="idx"
              type="button"
              class="tw-w-full tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-lg tw-px-3 tw-py-2 tw-text-left tw-text-sm tw-transition"
              :class="[
                isSelected(opt) ? 'tw-bg-slate-900/5 tw-text-slate-900 tw-font-medium' : 'tw-text-slate-700 hover:tw-bg-slate-900/5',
              ]"
              @click="selectOption(opt)"
            >
              <span class="tw-truncate">{{ optionLabel(opt) }}</span>
              <font-awesome-icon
                v-if="isSelected(opt)"
                icon="check"
                class="tw-h-4 tw-w-4 tw-text-slate-700 tw-flex-shrink-0"
              />
            </button>

            <div v-if="safeOptions.length === 0" class="tw-px-3 tw-py-2 tw-text-sm tw-text-slate-500">
              No options available
            </div>
          </div>
        </div>
      </Teleport>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, nextTick } from 'vue';

export default defineComponent({
  name: 'DropdownList',
  props: {
    textLabel: String,
    modelValue: [String, Number, Object, Array, null],
    options: {
      type: Array,
      default: () => [],
    },
    label: {
      type: String,
      default: undefined,
    },
    placeholder: {
      type: String,
      default: 'Select…',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    reduce: {
      type: Function,
      default: undefined,
    }
  },
  emits: ['update:modelValue', 'input'],
  data() {
    return {
      isOpen: false,
      menuStyle: {
        top: '0px',
        left: '0px',
        width: 'auto',
      } as Record<string, string>,
    };
  },
  computed: {
    safeOptions(): any[] {
      return Array.isArray(this.options) ? this.options : [];
    },
    selectedLabel(): string {
      const selected = this.safeOptions.find((opt: any) => this.isSelected(opt));
      return selected ? this.optionLabel(selected) : '';
    },
  },
  mounted() {
    document.addEventListener('click', this.onDocumentClick, true);
    window.addEventListener('resize', this.repositionMenu, true);
    window.addEventListener('scroll', this.repositionMenu, true);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDocumentClick, true);
    window.removeEventListener('resize', this.repositionMenu, true);
    window.removeEventListener('scroll', this.repositionMenu, true);
  },
  methods: {
    toggle() {
      if (this.disabled) return;
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.repositionMenu();
      }
    },
    close() {
      this.isOpen = false;
    },
    repositionMenu() {
      if (!this.isOpen) return;
      nextTick(() => {
        const buttonEl = this.$refs.buttonEl as HTMLElement | undefined;
        if (!buttonEl) return;
        const rect = buttonEl.getBoundingClientRect();
        const margin = 8;
        
        this.menuStyle = {
          top: `${Math.round(rect.bottom + margin)}px`,
          left: `${Math.round(rect.left)}px`,
          width: `${Math.round(rect.width)}px`,
        };
      });
    },
    optionLabel(opt: any): string {
      if (opt == null) return '';
      if (typeof opt === 'string' || typeof opt === 'number') return String(opt);
      const key = this.label;
      if (key && typeof opt === 'object' && key in opt) return String(opt[key]);
      if (typeof opt === 'object' && 'name' in opt) return String(opt.name);
      return String(opt);
    },
    optionValue(opt: any) {
      if (this.reduce && typeof this.reduce === 'function') {
        return this.reduce(opt);
      }
      return opt;
    },
    isSelected(opt: any): boolean {
      const v = this.modelValue;
      const ov = this.optionValue(opt);
      return v === ov;
    },
    selectOption(opt: any) {
      const val = this.optionValue(opt);
      this.$emit('update:modelValue', val);
      this.$emit('input', val);
      this.close();
    },
    onDocumentClick(e: MouseEvent) {
      if (!this.isOpen) return;
      const root = this.$refs.root as HTMLElement | undefined;
      const menuEl = this.$refs.menuEl as HTMLElement | undefined;
      if (root && e.target instanceof Node && root.contains(e.target)) return;
      if (menuEl && e.target instanceof Node && menuEl.contains(e.target)) return;
      this.close();
    },
  }
});
</script>